import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt, pct, biasColor, dirColor } from "@/lib/format";
import { DashboardLayout } from "@/components/DashboardLayout";
import { DemoBadge } from "@/components/DemoBadge";
import { EmptyState } from "@/components/Bits";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";

export default function AppWatchlist() {
  const [items, setItems] = useState([]);
  const [assets, setAssets] = useState([]);
  const [add, setAdd] = useState("");

  const load = () => api.get("/watchlist").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); api.get("/markets").then((r) => setAssets(r.data.assets)).catch(() => {}); }, []);

  const addItem = async () => {
    if (!add) return;
    await api.post("/watchlist", { symbol: add });
    toast.success(`${add} added`); setAdd(""); load();
  };
  const remove = async (s) => { await api.delete(`/watchlist/${encodeURIComponent(s)}`); load(); };

  return (
    <DashboardLayout title="Watchlist">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <DemoBadge />
        <div className="ml-auto flex items-center gap-2">
          <select value={add} onChange={(e) => setAdd(e.target.value)} data-testid="watch-select" className="rounded-lg border border-border bg-card px-3 py-2 text-sm">
            <option value="">Add asset…</option>
            {assets.filter((a) => !items.find((i) => i.symbol === a.symbol)).map((a) => <option key={a.symbol} value={a.symbol}>{a.symbol}</option>)}
          </select>
          <button onClick={addItem} data-testid="watch-add" className="flex items-center gap-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground"><Plus className="h-4 w-4" /> Add</button>
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState title="Your watchlist is empty" description="Add assets to track price, trend, AI bias and the latest signal." testid="watchlist-empty" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="watchlist-table">
            <thead><tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">Asset</th><th className="px-4 py-3 text-right">Price</th><th className="px-4 py-3 text-right">Change</th>
              <th className="px-4 py-3 text-right">Trend</th><th className="px-4 py-3 text-right">AI Bias</th><th className="px-4 py-3 text-right">Signal</th>
              <th className="px-4 py-3 text-right">Conf.</th><th className="px-4 py-3"></th>
            </tr></thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.symbol} className="border-b border-border last:border-0" data-testid={`watch-row-${it.symbol.replace(/\W/g, "-").toLowerCase()}`}>
                  <td className="px-4 py-3"><div className="font-display font-semibold">{it.symbol}</div><div className="text-xs text-muted-foreground">{it.name}</div></td>
                  <td className="px-4 py-3 text-right font-mono">{fmt(it.price)}</td>
                  <td className={`px-4 py-3 text-right font-mono ${it.change_pct >= 0 ? "text-bullish" : "text-bearish"}`}>{pct(it.change_pct)}</td>
                  <td className={`px-4 py-3 text-right capitalize ${biasColor(it.trend)}`}>{it.trend}</td>
                  <td className={`px-4 py-3 text-right font-semibold capitalize ${biasColor(it.ai_bias)}`}>{it.ai_bias}</td>
                  <td className={`px-4 py-3 text-right text-xs font-semibold ${dirColor(it.latest_signal)}`}>{it.latest_signal}</td>
                  <td className="px-4 py-3 text-right font-mono">{it.confidence}</td>
                  <td className="px-4 py-3 text-right"><button onClick={() => remove(it.symbol)} data-testid={`watch-remove-${it.symbol.replace(/\W/g, "-").toLowerCase()}`} className="text-muted-foreground hover:text-bearish"><X className="h-4 w-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </DashboardLayout>
  );
}
